import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { Activity, ChevronRight, Shield, AlertTriangle, BarChart2 } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import {
  whatsappAuditService,
  AuditAccountSnapshot,
  BreakerPause,
} from '@/services/whatsapp-broadcasts.service';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';

const QUALITY_VALUE: Record<string, number> = { GREEN: 3, YELLOW: 2, RED: 1 };
const QUALITY_TONE: Record<string, string> = {
  GREEN: 'text-success bg-success-soft',
  YELLOW: 'text-warning bg-warning-soft',
  RED: 'text-danger bg-danger-soft',
};
const PAUSE_REASON_FRIENDLY: Record<string, string> = {
  'failure-rate-breaker': 'Failure rate exceeded 15% over the last 100 sends',
  'block-rate-breaker':   'Block-rate proxy exceeded 2% over the last 500 sends',
  'quality-RED':          'Quality rating dropped to RED',
  'template-paused':      'Template paused by Meta',
  manual:                 'Paused manually by an operator',
};

const TIER_LIMIT: Record<string, number> = {
  TIER_50: 50, TIER_250: 250, TIER_1K: 1000, TIER_10K: 10000, TIER_100K: 100000,
};

type Props = {
  /** Rendered inside WhatsappToolsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function AuditDashboard({ embedded = false }: Props = {}) {
  const [accounts, setAccounts] = useState<WhatsappAccount[]>([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [snapshot, setSnapshot] = useState<AuditAccountSnapshot | null>(null);
  const [breakers, setBreakers] = useState<BreakerPause[]>([]);
  const [loading, setLoading] = useState(false);

  // Bootstrap account list — pick the default first.
  useEffect(() => {
    whatsappAccountService.list().then((rows) => {
      setAccounts(rows);
      const def = rows.find((a) => a.isDefault) ?? rows[0];
      if (def) setSelectedUid(def.accountUid);
    });
  }, []);

  useEffect(() => {
    if (!selectedUid) return;
    setLoading(true);
    Promise.all([
      whatsappAuditService.accountSnapshot(selectedUid).catch(() => null),
      whatsappAuditService.breakerHistory(50).catch(() => ({ items: [], total: 0 })),
    ]).then(([snap, br]) => {
      setSnapshot(snap);
      setBreakers(br.items);
      setLoading(false);
    });
  }, [selectedUid]);

  const headroomPct = useMemo(() => {
    if (!snapshot?.messagingLimitTier) return 0;
    const cap = TIER_LIMIT[snapshot.messagingLimitTier] ?? 50;
    if (cap === 0) return 0;
    return Math.min(100, Math.round((snapshot.todayUniqueCount / cap) * 100));
  }, [snapshot]);

  const qualityChartData = useMemo(
    () =>
      (snapshot?.qualityHistory ?? []).map((h) => ({
        at: new Date(h.at).getTime(),
        rating: QUALITY_VALUE[h.rating] ?? 2,
        ratingLabel: h.rating,
      })),
    [snapshot],
  );

  return (
    <div>
      {!embedded && (
        <Topbar
          title="WhatsApp safety audit"
          description="Quality rating + tier history, headroom, and recent breaker pauses for the selected WhatsApp account."
        />
      )}

      <div className={clsx('space-y-5 px-7', embedded ? 'pb-24 pt-5' : 'py-5')}>
        {/* Account picker */}
        <Card padded>
          <label className="mb-1.5 block text-xs font-medium text-text-secondary">WhatsApp account</label>
          <Select
            value={selectedUid}
            onChange={(e) => setSelectedUid(e.target.value)}
            disabled={accounts.length === 0}
          >
            {accounts.length === 0 && <option value="">No accounts</option>}
            {accounts.map((a) => (
              <option key={a.accountUid} value={a.accountUid}>
                {a.businessName} · {a.displayPhoneNumber}{a.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </Select>
        </Card>

        {loading && (
          <Card padded>
            <div className="py-8 text-center text-sm text-text-muted">Loading…</div>
          </Card>
        )}

        {snapshot && !loading && (
          <>
            {/* Top KPI row */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label="Quality rating"
                value={snapshot.qualityRating ?? '—'}
                tone={QUALITY_TONE[snapshot.qualityRating ?? ''] ?? ''}
                icon={<Shield size={16} />}
              />
              <KpiCard
                label="Tier"
                value={snapshot.messagingLimitTier ?? '—'}
                tone="text-info bg-info-soft"
                icon={<BarChart2 size={16} />}
              />
              <KpiCard
                label="Today (unique recipients)"
                value={`${snapshot.todayUniqueCount.toLocaleString()} / ${(TIER_LIMIT[snapshot.messagingLimitTier ?? ''] ?? 50).toLocaleString()}`}
                subtitle={`${headroomPct}% of cap`}
                tone={headroomPct >= 90 ? 'text-warning bg-warning-soft' : 'text-text-secondary bg-surface-alt'}
                icon={<Activity size={16} />}
              />
              <KpiCard
                label="Lifetime sent"
                value={snapshot.totalLifetimeSent.toLocaleString()}
                tone="text-text-secondary bg-surface-alt"
                icon={<BarChart2 size={16} />}
              />
            </div>

            {/* Cooldown banner */}
            {snapshot.cooldownUntil && new Date(snapshot.cooldownUntil) > new Date() && (
              <Card padded className="border-warning bg-warning-soft/40">
                <div className="flex items-start gap-2.5">
                  <AlertTriangle size={16} className="mt-0.5 text-warning" />
                  <div>
                    <div className="text-sm font-semibold text-text-primary">Account is in cooldown</div>
                    <div className="text-xs text-text-muted">
                      Auto-paused until {new Date(snapshot.cooldownUntil).toLocaleString()}.
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Quality history graph */}
            <Card padded>
              <h3 className="text-sm font-semibold text-text-primary mb-2">Quality history</h3>
              <p className="text-2xs text-text-muted mb-3">
                Last {snapshot.qualityHistory.length} rating transitions. Higher = better
                (3=GREEN, 2=YELLOW, 1=RED). Anything dipping toward RED for more than ~7 days
                triggers a tier downgrade by Meta.
              </p>
              {qualityChartData.length === 0 ? (
                <div className="py-6 text-center text-xs text-text-muted">
                  No quality transitions recorded yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={qualityChartData}>
                    <XAxis
                      dataKey="at"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(t: number) => new Date(t).toLocaleDateString()}
                    />
                    <YAxis domain={[0.8, 3.2]} ticks={[1, 2, 3]} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(v, _n, item: any) => [item?.payload?.ratingLabel ?? String(v), 'Rating']}
                      labelFormatter={(t) => (typeof t === 'number' ? new Date(t).toLocaleString() : String(t ?? ''))}
                    />
                    <Line type="monotone" dataKey="rating" stroke="#10b981" strokeWidth={2} dot />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Tier history timeline */}
            <Card padded>
              <h3 className="text-sm font-semibold text-text-primary mb-2">Tier history</h3>
              {snapshot.tierHistory.length === 0 ? (
                <div className="py-6 text-center text-xs text-text-muted">No tier changes recorded.</div>
              ) : (
                <ol className="space-y-2">
                  {snapshot.tierHistory.slice().reverse().map((h, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm">
                      <span className="inline-flex rounded-full bg-info-soft px-2.5 py-0.5 text-2xs font-semibold text-info">
                        {h.tier}
                      </span>
                      <span className="text-text-muted text-xs">
                        {new Date(h.at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            {/* Recent breaker pauses */}
            <Card padded>
              <h3 className="text-sm font-semibold text-text-primary mb-3 flex items-center gap-2">
                <AlertTriangle size={14} /> Recent auto-pauses
              </h3>
              {breakers.length === 0 ? (
                <div className="py-4 text-center text-xs text-text-muted">No breaker pauses recorded — looking healthy.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-2xs font-semibold uppercase tracking-wider text-text-muted">
                        <th className="px-3 py-2.5">Broadcast</th>
                        <th className="px-3 py-2.5">Reason</th>
                        <th className="px-3 py-2.5">Paused at</th>
                        <th className="px-3 py-2.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakers.map((b) => (
                        <tr key={b.broadcastUid} className="border-b border-border/50 last:border-0 hover:bg-surface-alt/50">
                          <td className="px-3 py-2.5 text-text-primary">{b.name}</td>
                          <td className="px-3 py-2.5 text-text-secondary">
                            {PAUSE_REASON_FRIENDLY[b.pauseReason] ?? b.pauseReason}
                          </td>
                          <td className="px-3 py-2.5 text-text-muted text-xs">
                            {new Date(b.pausedAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <Link
                              to={`/whatsapp-account/bulk/${b.broadcastUid}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                              View <ChevronRight size={12} />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label, value, subtitle, tone, icon,
}: {
  label: string;
  value: string;
  subtitle?: string;
  tone: string;
  icon: React.ReactNode;
}) {
  return (
    <Card padded>
      <div className="flex items-start justify-between">
        <span className="text-2xs font-medium uppercase tracking-wider text-text-muted">{label}</span>
        <span className={`inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold ${tone}`}>{icon}</span>
      </div>
      <div className="mt-2 text-xl font-semibold text-text-primary tabular-nums">{value}</div>
      {subtitle && <div className="mt-0.5 text-xs text-text-muted">{subtitle}</div>}
    </Card>
  );
}
